import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import SectionHeader from "../components/SectionHeader";
import { submitEnquiry, readUtmParams } from "../lib/api";

const variants = [
  { value: "degustace-kratka", label: "Krátká degustace pro 2 (od 600 Kč)" },
  { value: "degustace-rozsirena", label: "Rozšířená degustace pro 2 (od 900 Kč)" },
  { value: "degustace-velka", label: "Velká degustace pro 2 (od 1 200 Kč)" },
  { value: "rodinny-pobyt-na-jizni-morave", label: "Rodinný pobyt na jižní Moravě (9 990 Kč / 4 osoby / 5 nocí)" },
  { value: "piknik-vinohrad", label: "Piknik ve vinohradě" },
  { value: "individual", label: "Jinou částku / na míru" },
];

export default function DarkovyPoukaz() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", voucher_variant: variants[0].value,
    voucher_recipient: "", message: "", consent_privacy: false, consent_adult: false, website: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_adult) {
      return setStatus({ loading: false, ok: false, error: "Potvrďte prosím, že je vám alespoň 18 let." });
    }
    setStatus({ loading: true, ok: false, error: "" });
    const res = await submitEnquiry({
      type: "darkovy_poukaz",
      name: form.name, email: form.email, phone: form.phone,
      message: form.message, voucher_variant: form.voucher_variant, voucher_recipient: form.voucher_recipient,
      consent_privacy: true, consent_adult: true, website: form.website,
      ...readUtmParams(),
    });
    if (res.ok) setStatus({ loading: false, ok: true, error: "" });
    else setStatus({ loading: false, ok: false, error: res.error });
  };

  return (
    <main id="main">
      <Seo path="/darkovy-poukaz" title="Dárkový poukaz na víno a zážitky | Vinařství Brychta"
        description="Darujte zážitek – degustaci, víkend nebo piknik ve vinohradě. Poukaz zašleme e-mailem k vytištění po potvrzení objednávky." />
      <Breadcrumbs items={[{ label: "Dárkový poukaz" }]} />
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 py-14 md:py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader as="h1" eyebrow="Dárek u vína" title="Darujte zážitek, ne věc"
            kicker="Poukaz na degustaci, pobyt u vinaře nebo piknik. Po potvrzení vám pošleme PDF k vytištění – ideální jako narozeninový nebo vánoční dárek." />
          <ul className="mt-6 space-y-2 text-charcoal/80 text-sm">
            <li>• Platnost 12 měsíců od vystavení</li>
            <li>• Konkrétní termín dohodneme s obdarovaným</li>
            <li>• Vhodné pro páry, rodiny i pracovní týmy</li>
            <li>• PDF poukaz k vytištění po ručním potvrzení</li>
          </ul>
        </div>

        <div className="lg:col-span-7 bg-cream p-6 md:p-10">
          {status.ok ? (
            <div className="bg-ivory border border-burgundy/20 p-6" data-testid="voucher-success">
              <div className="font-serif text-2xl text-burgundy">Děkujeme, poptávka je přijatá.</div>
              <p className="mt-2 text-charcoal/80">Ozveme se s potvrzením, platebními údaji a následně vám pošleme PDF poukaz k vytištění.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="voucher-form" noValidate>
              <input type="text" name="website" value={form.website} onChange={(e) => update("website", e.target.value)} className="hidden" tabIndex={-1} aria-hidden="true" />
              <Field label="Varianta poukazu">
                <select value={form.voucher_variant} onChange={(e) => update("voucher_variant", e.target.value)} data-testid="voucher-variant" className="voucher-input">
                  {variants.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </Field>
              <Field label="Pro koho poukaz je (nepovinné)">
                <input placeholder={'Např. „Pro Janu, k narozeninám"'} value={form.voucher_recipient} onChange={(e) => update("voucher_recipient", e.target.value)} className="voucher-input" data-testid="voucher-recipient" />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Vaše jméno" required>
                  <input required value={form.name} onChange={(e) => update("name", e.target.value)} className="voucher-input" data-testid="voucher-name" />
                </Field>
                <Field label="Váš e-mail" required>
                  <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="voucher-input" data-testid="voucher-email" />
                </Field>
              </div>
              <Field label="Telefon" required>
                <input required value={form.phone} onChange={(e) => update("phone", e.target.value)} className="voucher-input" data-testid="voucher-phone" />
              </Field>
              <Field label="Poznámka (věnování, částka na míru…)">
                <textarea rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} className="voucher-input" data-testid="voucher-message" />
              </Field>

              <label className="flex items-start gap-3 text-sm text-charcoal/70" data-testid="voucher-privacy-notice">
                <span>
                  Odesláním formuláře berete na vědomí zpracování uvedených údajů za účelem vyřízení této žádosti o poukaz.
                  Podrobnosti najdete v&nbsp;<Link to="/ochrana-osobnich-udaju" className="underline hover:text-burgundy">ochraně osobních údajů</Link>.
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-charcoal/75">
                <input type="checkbox" checked={form.consent_adult} onChange={(e) => update("consent_adult", e.target.checked)} className="mt-1" data-testid="voucher-adult" />
                <span>Potvrzuji, že jsem starší 18 let.</span>
              </label>

              {status.error && <div className="text-sm text-red-700" role="alert">{status.error}</div>}
              <button disabled={status.loading} type="submit" data-testid="voucher-submit" className="bg-burgundy hover:bg-burgundy-hover text-ivory px-8 py-3.5 tracking-wide disabled:opacity-60">
                {status.loading ? "Odesílám…" : "Objednat poukaz"}
              </button>
              <style>{`.voucher-input{width:100%;background:#FCFAF6;border:1px solid rgba(37,35,31,.18);padding:.65rem .8rem;font-size:.95rem;color:#25231F;border-radius:2px}.voucher-input:focus{outline:2px solid #B6955A;outline-offset:1px}`}</style>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">{label}{required && <span className="text-burgundy"> *</span>}</span>
      {children}
    </label>
  );
}
